import { inject, Injectable } from "@angular/core";
import { ComparatorOperators, ExportEvaluationTreeGQL, ExportEvaluationTreeMutation, GetEvaluationOverallGQL, GetEvaluationOverallQuery, GetEvaluationTreeGQL, GetEvaluationTreeQuery, GetRiskMapTreeFiltersGQL, GetRiskMapTreeFiltersQuery, GetRiskOverallGQL, GetRiskOverallQuery, IsLegalEntityEvaluationMassiveEditInProgressGQL, IsLegalEntityEvaluationMassiveEditInProgressQuery, OperatorInput, OrderTypeInput, PagingInput, RequestMassiveEditGQL, RequestMassiveEditMutation, RiskMapTreeColumns, RiskMapTreeQueryComplexInput, RiskMapTreeSimpleQueryInput, RiskMapTreeSortInput, UpdateEvaluationTreeGQL, UpdateEvaluationTreeInput, UpdateEvaluationTreeMutation, UpdateTreeNoteEvaluationGQL, UpdateTreeNoteEvaluationMutation } from "../../../graphql/generated";
import { map, Observable, catchError, of } from "rxjs";
import { MutationData, unwrapMutationData } from "../shared/graphql-mutation-response.util";

@Injectable({
    providedIn: 'root'
})
export class EvaluationTreeService {
    private readonly GetEvaluationTreeGQL: GetEvaluationTreeGQL = inject(GetEvaluationTreeGQL);
    private readonly GetEvaluationOverallGQL: GetEvaluationOverallGQL = inject(GetEvaluationOverallGQL);
    private readonly UpdateEvaluationTreeGQL: UpdateEvaluationTreeGQL = inject(UpdateEvaluationTreeGQL);
    private readonly ExportEvaluationTreeGQL: ExportEvaluationTreeGQL = inject(ExportEvaluationTreeGQL);
    private readonly GetRiskOverallGQL: GetRiskOverallGQL = inject(GetRiskOverallGQL);
    private readonly UpdateTreeNoteEvaluationGQL: UpdateTreeNoteEvaluationGQL = inject(UpdateTreeNoteEvaluationGQL);
    private readonly GetRiskMapTreeFiltersGQL: GetRiskMapTreeFiltersGQL = inject(GetRiskMapTreeFiltersGQL);
    private readonly RequestMassiveEditGQL: RequestMassiveEditGQL = inject(RequestMassiveEditGQL);
    private readonly IsLegalEntityEvaluationMassiveEditInProgressGQL: IsLegalEntityEvaluationMassiveEditInProgressGQL = inject(IsLegalEntityEvaluationMassiveEditInProgressGQL);

    getEvaluationTree(
        campaignId: string,
        legalEntityId: string,
        parameters: any,
        au: boolean | null,
        completed: boolean | null
    ): Observable<GetEvaluationTreeQuery['evaluationTree']> {
        const par = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetEvaluationTreeGQL.fetch({ campaignId, legalEntityId, query: par.query as RiskMapTreeQueryComplexInput, sort: par.sort as RiskMapTreeSortInput, au, completed, page: par.page }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.evaluationTree))
    }

    getAllEvaluationTree(
        campaignId: string,
        legalEntityId: string
    ): Observable<GetRiskMapTreeFiltersQuery['riskMapTreeFields']> {
        return this.GetRiskMapTreeFiltersGQL.fetch({ campaignId, legalEntityId, au: null, active: true }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskMapTreeFields))
    }

    getEvaluationOverall(
        legalEntityId: string,
        campaignId: string,
        parameters: any
    ): Observable<GetEvaluationOverallQuery['evaluationTree']> {
        const par = convertLazyLoadToComplexWithoutPage(parameters);
        return this.GetEvaluationOverallGQL.fetch({ campaignId, legalEntityId, query: par.query as RiskMapTreeQueryComplexInput, sort: par.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.evaluationTree))
    }

    updateEvaluation(
        riskMapId: string,
        riskId: string,
        impactId: string,
        likelihoodId: string,
        icsId: string,
        filters: boolean
    ): Observable<MutationData<UpdateEvaluationTreeMutation['updateEvaluationTree']>> {
        const updateEvaluationRequest: UpdateEvaluationTreeInput[] = [{ riskId, riskMapId, icsId, impactId, likelihoodId }]
        return this.UpdateEvaluationTreeGQL.mutate({ updateEvaluationRequest, filters }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateEvaluationTree), unwrapMutationData('Unable to update the evaluation'))
    }

    updateEvaluationMultiple(
        updateEvaluationRequest: UpdateEvaluationTreeInput[]
    ): Observable<MutationData<UpdateEvaluationTreeMutation['updateEvaluationTree']>> {
        return this.UpdateEvaluationTreeGQL.mutate({ updateEvaluationRequest }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateEvaluationTree), unwrapMutationData('Unable to update the evaluation'))
    }

    exportEvaluationTree(
        campaignId: string,
        legalEntityId: string,
        parameters: any,
        timeZone: string
    ): Observable<ExportEvaluationTreeMutation['downloadEvaluationTree']> {
        const param = convertLazyLoadToComplexWithoutPage(parameters);
        return this.ExportEvaluationTreeGQL.mutate({ campaignId, legalEntityId, timeZone, query: param.query as RiskMapTreeQueryComplexInput, sort: param.sort as RiskMapTreeSortInput }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadEvaluationTree))
    }

    updateTreeNote(
        campaignId: string,
        legalEntityId: string,
        processCode: string,
        note: string,
        filter: boolean
    ): Observable<MutationData<UpdateTreeNoteEvaluationMutation['updateTreeNote']>> {
        return this.UpdateTreeNoteEvaluationGQL.mutate({ campaignId, legalEntityId, processCode, note, filter }, { fetchPolicy: 'no-cache' }).pipe(map(res => (res.data?.updateTreeNote)), unwrapMutationData('Unable to update the note'))
    }

    getRiskOverall(campaignId: string, legalEntityId: string): Observable<GetRiskOverallQuery['riskOverall']> {
        return this.GetRiskOverallGQL.fetch({ campaignId, legalEntityId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.riskOverall))
    }

    requestMassiveEdit(riskMapTreeValueIds: string[], icsId: string, impactId: string, likelihoodId: string): Observable<MutationData<RequestMassiveEditMutation['requestMassiveEdit']>> {
        return this.RequestMassiveEditGQL.mutate({ icsId, impactId, likelihoodId, riskMapTreeValueIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.requestMassiveEdit), unwrapMutationData('Unable to start the massive edit'))
    }

    isLegalEntityEvaluationMassiveEditInProgress(campaignId: string, legalEntityId: string): Observable<IsLegalEntityEvaluationMassiveEditInProgressQuery['isLegalEntityEvaluationMassiveEditInProgress']> {
        return this.IsLegalEntityEvaluationMassiveEditInProgressGQL.fetch({ campaignId, legalEntityId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.isLegalEntityEvaluationMassiveEditInProgress))
    }
}

export function convertLazyLoadToComplexWithoutPage(parameters: any): { query: RiskMapTreeQueryComplexInput, sort: RiskMapTreeSortInput, page: PagingInput } {
    const query: RiskMapTreeSimpleQueryInput[] = [];
    const queryRisk: RiskMapTreeSimpleQueryInput[] = [];

    const fieldsNotBoolean = ['level', 'name', 'code', 'cluster', 'note', 'processNote', 'status', 'processStatus', 'auditArea', 'auditSubArea'];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                if (values === null || !Array.isArray(values) || values.length === 0) return;
                (ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP] || ENUM_RISKMAP.riskParameterName).forEach(key => {
                    if (key === 'RISK_PARAMETER_NAME') return;
                    let filteredValue: any[] = [];
                    if (fieldsNotBoolean.includes(field)) {
                        filteredValue = values;
                    } else if (key === RiskMapTreeColumns.EvaluationStatus) {
                        filteredValue = values.length > 1 ? [] : values[0] === 'In Progress' ? ['false'] : ['true'];
                    } else {
                        filteredValue = values.length > 1 ? [] : values[0] === 'Active' || values[0] === 'Yes' ? ['true'] : ['false'];
                    }
                    query.push({
                        field: key as RiskMapTreeColumns,
                        filters: {
                            operator: key === RiskMapTreeColumns.Level ? ComparatorOperators.Eq : ComparatorOperators.Match,
                            value: filteredValue
                        }
                    });
                });
            });

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values === null || !Array.isArray(values) || values.length === 0) return;
                (ENUM_RISKMAP[field as keyof typeof ENUM_RISKMAP] || ENUM_RISKMAP.riskParameterName).forEach(key => {

                    let filteredValue: any[] = [];
                    if (key === 'RISK_PARAMETER_NAME' && values.length === 1) {
                        filteredValue = [field, values[0] === 'Active' || values[0] === 'Yes' ? 'true' : 'false']
                    }

                    if (key === 'RISK_PARAMETER_NAME' && values.length === 1)
                        queryRisk.push({
                            field: key as RiskMapTreeColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: filteredValue
                            }
                        });
                })
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                },
                {
                    query: queryRisk,
                    operator: OperatorInput.And
                }
            ]
        },
        sort: {
            sortField: ENUM_RISKMAP?.[parameters?.order?.sortField as keyof typeof ENUM_RISKMAP]?.[0] as RiskMapTreeColumns || RiskMapTreeColumns.Sort,
            sortOrder: parameters?.order?.sortDirection === 'ASC' ? OrderTypeInput.Asc : OrderTypeInput.Desc
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 3
        }
    }
}

export const ENUM_RISKMAP = {
    name: [RiskMapTreeColumns.Name],
    code: [RiskMapTreeColumns.Code],
    cluster: [RiskMapTreeColumns.Cluster],
    note: [RiskMapTreeColumns.Note],
    processNote: [RiskMapTreeColumns.Note],
    au: [RiskMapTreeColumns.Au],
    auditableUniverse: [RiskMapTreeColumns.Au],
    status: [RiskMapTreeColumns.RiskMapStatus],
    riskMapStatus: [RiskMapTreeColumns.RiskMapStatus],
    riskParameterName: ['LEVEL'],
    auditArea: [RiskMapTreeColumns.AuditDomain],
    auditSubArea: [RiskMapTreeColumns.AuditSubdomain],
    evaluationProgressStatus: [RiskMapTreeColumns.EvaluationStatus]
}
